import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryClientProvider, QueryClient } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { PersistGate } from 'redux-persist/integration/react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import App from './components/App';
import storeAndPersist from './store';
import environment from './environment';
import './styles/libs/bootstrap/css/bootstrap.min.css';
import './styles/libs/grammarly/styles.css';
import './styles/index.css';
import './styles/theme.css';
import { LOCAL } from 'constants/envTypes';
import * as ErrorService from 'services/ErrorService/ErrorService';
import { SENTRY_ORG_SLUG } from 'services/ErrorService/constants';
import SentryFullStory from '@sentry/fullstory';
import GrammarlyWrapper from './wrappers/GrammarlyWrapper';
import { initMonitoring } from '@asurion-core/frontend-utils/lib/WebObservability';
import { browserHistory } from './browserHistory';
import * as ReactRouter from 'react-router-dom';
import MonitoringErrorWrapper from './wrappers/ErrorWrapper';
import { ThemeProvider } from './context/ThemeContext';

if (process.env.REACT_APP_USE_MOCK === 'true') {
  const { worker } = require('./apiMocks/browser');
  worker.start({
    onUnhandledRequest: 'bypass',
  });
}

const pathMap = {
  '/web/home.html': '/',
  '/web/index.html': '/home',
  '/web/survey.html': window.location.href.replace('/web', ''),
};

if (environment.observability.isTelemetryEnabled) {
  initMonitoring({
    appName: 'simplr-web',
    environment: environment.app.currentEnv,
    url: environment.observability.telemetryUrl,
    release: environment.sentry.release || 'N/A',
    enableRoutesV5: true,
    reactRouteDom: ReactRouter,
    history: browserHistory,
  });
}

if (pathMap[window.location.pathname]) {
  window.location.href = pathMap[window.location.pathname];
}

const errorServiceInitProps = {
  dsn: environment.sentry.dsn,
  environment: environment.app.currentEnv,
  integrations: [new SentryFullStory(SENTRY_ORG_SLUG)],
  ignoreErrors: ErrorService.ErrorConstants.ignoreErrors,
};

if (environment.sentry.release) {
  ErrorService.init({
    ...errorServiceInitProps,
    release: environment.sentry.release,
  });
} else if (environment.app.currentEnv === LOCAL) {
  ErrorService.init(errorServiceInitProps);
}

const queryClient = new QueryClient();

const renderApp = async () => {
  createRoot(document.getElementById('root')).render(
    <QueryClientProvider client={queryClient}>
      <Provider store={storeAndPersist.store}>
        <DndProvider backend={HTML5Backend}>
          <PersistGate loading={null} persistor={storeAndPersist.persistor}>
            <GrammarlyWrapper>
              <MonitoringErrorWrapper>
                <ThemeProvider>
                  <App />
                </ThemeProvider>
              </MonitoringErrorWrapper>
            </GrammarlyWrapper>
          </PersistGate>
        </DndProvider>
      </Provider>
      {environment.devtools.isReactQueryDevtoolsEnabled && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

renderApp();

try {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
} catch (error) {
  ErrorService.handleError({ error, customMessage: 'Service worker un-registeration failed' });
}

// hot reloading
if (module.hot) {
  console.log('hot reloading enabled');
  module.hot.accept('./components/App', () => {
    renderApp();
  });
} else {
  console.log('hot reloading disabled');
}
